import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { AlertTriangle, ChevronDown, ChevronRight, Coins, Cpu, UserX } from "lucide-react";
import Card from "./Card";
import { useApi } from "../hooks/useApi";
import { asSentence } from "../utils/aiFleetPortfolio";
import SpendTrajectoryChart, { PERIODS } from "./SpendTrajectoryChart";

/**
 * What the AI estate consumed, and what it cost (§11a).
 *
 * The design constraint that shapes every number here: a spend figure is the one thing in this
 * module a customer will check against an invoice. So nothing is rounded into looking better than
 * it is — a day the feed could not read fully is excluded from the totals and *said*, an absent
 * cost renders as "not reported" rather than $0, and usage that belongs to no known agent gets its
 * own row instead of being folded away.
 */

const fmtTokens = (n) => {
  if (n == null) return "—";
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return `${n}`;
};

/**
 * Null is "the provider did not report a cost", which is not the same as zero.
 *
 * A null *currency* is a third state again: the row spans several, so no single symbol is right.
 * Defaulting to USD there would put a dollar sign on a euro at the last step, after the backend went
 * to some trouble to avoid exactly that — so it renders the amount without a symbol and says why.
 */
const fmtCost = (v, currency) => {
  if (v == null) return "Not reported";
  if (!currency) return `${v.toFixed(2)} (mixed currencies)`;
  return new Intl.NumberFormat(undefined, {
    style: "currency", currency, maximumFractionDigits: 2,
  }).format(v);
};

const fmtDay = (d) =>
  new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" });

function Stat({ icon: Icon, label, value, sub, tone = "text-content-primary" }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className={`w-5 h-5 mt-0.5 ${tone}`} />
      <div>
        <div className="text-xs text-content-secondary">{label}</div>
        <div className={`text-xl font-semibold ${tone}`}>{value}</div>
        {sub && <div className="text-xs text-content-secondary mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

/**
 * Projected spend.
 *
 * This is an operating-spend forecast and nothing else — not predicted cyber-loss, never summed with
 * FAIR exposure. They are different measures and a reader who conflates them has been misled by the
 * screen, so the wording keeps them apart.
 *
 * The design constraint throughout: a projection must look exactly as uncertain as it is. Every
 * figure shows its range, the method is on the page rather than in a design document, and when the
 * history cannot support a projection the card says why instead of drawing a confident line.
 */
function ForecastCard({ forecast, currency }) {
  if (!forecast?.available) {
    return (
      <Card>
        <div className="p-4">
          <h4 className="text-sm font-semibold text-content-primary mb-1">Projected spend</h4>
          <p className="text-sm text-content-secondary">
            {forecast?.unavailableReason
              || "Not enough settled history yet to project spend."}
          </p>
          {forecast?.daysWithData != null && (
            <p className="text-xs text-content-secondary mt-2">
              {forecast.daysWithData} of {forecast.minimumDaysRequired} day(s) recorded.
            </p>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h4 className="text-sm font-semibold text-content-primary">Projected spend</h4>
            <p className="text-xs text-content-secondary">
              Operating spend only — not related to financial risk exposure.
            </p>
          </div>
          {forecast.trendIsMeaningful ? (
            <span className="text-xs text-content-secondary shrink-0">
              {forecast.trendPerDayUsd >= 0 ? "▲" : "▼"}{" "}
              {fmtCost(Math.abs(forecast.trendPerDayUsd), currency)}/day
            </span>
          ) : (
            <span className="text-xs text-content-secondary shrink-0">no clear trend</span>
          )}
        </div>

        {/* Stated rather than silently skipped: an organization that set a budget is entitled to
            know why it is not being checked. */}
        {forecast.budgetNotApplicableReason && (
          <div className="mb-3 p-2 rounded-md border border-border text-xs text-content-secondary">
            {forecast.budgetNotApplicableReason}
          </div>
        )}

        {forecast.budgetUsd != null && (
          <div
            className={`mb-3 p-2 rounded-md border text-xs ${
              forecast.projectedToExceedBudget
                ? "border-red-200 bg-red-50 text-red-800"
                : "border-border text-content-secondary"
            }`}
          >
            {forecast.projectedToExceedBudget ? (
              <>
                Projected to exceed the {fmtCost(forecast.budgetUsd, currency)} monthly budget —
                {" "}{fmtCost(forecast.projectedMonthEndUsd, currency)} by month end,
                {" "}{fmtCost(forecast.monthToDateUsd, currency)} spent so far. Stated only because
                even the low end of the range goes over.
              </>
            ) : (
              <>
                {fmtCost(forecast.monthToDateUsd, currency)} of the
                {" "}{fmtCost(forecast.budgetUsd, currency)} monthly budget spent;
                {" "}{fmtCost(forecast.projectedMonthEndUsd, currency)} projected by month end.
              </>
            )}
          </div>
        )}

        <table className="w-full text-sm mb-3">
          <thead>
            <tr className="text-xs text-content-secondary text-left">
              <th className="pb-2">Horizon</th>
              <th className="pb-2 text-right">Expected</th>
              <th className="pb-2 text-right">Range</th>
            </tr>
          </thead>
          <tbody>
            {(forecast.projections || []).map((p) => (
              <tr key={p.label} className="border-t border-border">
                <td className="py-1.5 text-content-primary">{p.label}</td>
                <td className="py-1.5 text-right">{fmtCost(p.expectedUsd, currency)}</td>
                {/* The range is not a footnote. A single expected figure reads as a promise. */}
                <td className="py-1.5 text-right text-content-secondary">
                  {fmtCost(p.lowUsd, currency)} – {fmtCost(p.highUsd, currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {(forecast.contributors || []).length > 0 && (
          <div className="mb-3">
            <h5 className="text-xs font-semibold text-content-primary mb-1">What changed</h5>
            <div className="space-y-1">
              {forecast.contributors.map((c) => (
                <div key={c.aiAssetId ?? "unattributed"} className="flex justify-between text-xs">
                  <span className="text-content-primary">
                    {c.name}
                    {c.aiAssetId == null && (
                      <span className="ml-1.5 text-orange-600">unattributed</span>
                    )}
                  </span>
                  <span className={c.changeUsd >= 0 ? "text-amber-700" : "text-content-secondary"}>
                    {c.changeUsd >= 0 ? "+" : "−"}
                    {fmtCost(Math.abs(c.changeUsd), currency)} vs the fortnight before
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-[11px] text-content-secondary border-t border-border pt-2">
          {forecast.method} Based on {forecast.daysWithData} settled day(s); days the provider may
          still restate are excluded.
        </p>
      </div>
    </Card>
  );
}

/**
 * Per-seat AI licensing.
 *
 * Kept apart from token spend and never added to it. A seat is billed per person per month whether
 * or not anybody types a word, so the two have different units — a combined total would be a number
 * with no meaning, which is the distinction the ledger declined to blur.
 *
 * For most enterprises this is where the majority of AI money goes, and until now the product could
 * see none of it.
 */
function SeatLicensingCard({ seats }) {
  return (
    <Card>
      <div className="p-4">
        <h4 className="text-sm font-semibold text-content-primary mb-1">Seat licensing</h4>
        <p className="text-xs text-content-secondary mb-3">
          Licensed per person per month, so it is billed whether or not the seat is used — and
          counted separately from token spend above, which is charged per call.
        </p>

        {(seats.skus || []).map((sku) => (
          <div key={sku.skuId} className="mb-3 last:mb-0">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm text-content-primary">{sku.skuName || sku.skuId}</span>
              <span className="text-sm font-semibold text-content-primary">
                {sku.assignedSeats} seat{sku.assignedSeats === 1 ? "" : "s"}
              </span>
            </div>

            <div className="text-xs text-content-secondary mt-0.5">
              <div>
                {sku.billedSeatsArePurchased
                  ? `${sku.assignedSeats} of ${sku.billedSeats} purchased seats assigned`
                  : `${sku.assignedSeats} assigned (purchased count not reported)`}
                {sku.unassignedSeats > 0 && (
                  <span className="text-orange-600">
                    {" "}· {sku.unassignedSeats} paid for and given to nobody
                  </span>
                )}
              </div>

              {/* The distinction the reader works hardest to preserve: unknown is not zero. Showing
                  "0 used" for a tenant that conceals names would be a dramatic and false finding. */}
              {sku.activityReadable ? (
                <div>
                  {sku.activeSeats} used in the last {sku.activityWindowDays} days
                  {sku.neverUsedSeats > 0 && (
                    <span className="text-orange-600">
                      {" "}· {sku.neverUsedSeats} never used
                    </span>
                  )}
                </div>
              ) : (
                <div className="flex items-start gap-1.5 mt-1">
                  <AlertTriangle className="w-3 h-3 text-amber-600 mt-0.5 shrink-0" />
                  <span>{sku.activityUnavailableReason}</span>
                </div>
              )}

              {sku.declaredMonthlyCost != null && (
                <div className="mt-1">
                  {fmtCost(sku.declaredMonthlyCost, sku.currency)}/month at the seat price you
                  declared
                  {sku.declaredWastedCost != null && sku.declaredWastedCost > 0 && (
                    <span className="text-orange-600">
                      {" "}· {sku.wastedSeatsArePartial ? "at least " : ""}
                      {fmtCost(sku.declaredWastedCost, sku.currency)} on seats nobody has used
                      {/* Partial when activity is unreadable: the unassigned half is certain, the
                          inactive half is unknown, so the figure is a floor rather than a total. */}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        <p className="text-[11px] text-content-secondary border-t border-border pt-2 mt-3">
          {/* Said explicitly, because a figure on a page reads as measured unless it says otherwise
              — and no provider exposes a negotiated seat price through an API. */}
          Seat costs are the price your organisation entered, not a figure any provider reported.
          Set or change it in AI settings.
        </p>
      </div>
    </Card>
  );
}

export default function AiUsageCostPanel({ orgId, modelUsage = [], modelUsageUnavailableReason = null }) {
  const apiCall = useApi();
  // The summary can send modelUsage as null (withheld) with a reason; a default parameter only
  // covers undefined, so normalise here rather than crash on .length.
  const models = Array.isArray(modelUsage) ? modelUsage : [];
  const modelsWithheld = modelUsage === null && !!modelUsageUnavailableReason;
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [seats, setSeats] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // Consumption | Forecast | Licensing — three questions that used to stack into one scroll.
  const [view, setView] = useState("consumption");
  // Tokens/Spend toggle on the consumption chart: one chart, one axis at a time, half the height
  // of the stacked pair it replaces.
  const [chartMode, setChartMode] = useState("tokens");
  const [showByModel, setShowByModel] = useState(false);
  const [showByAsset, setShowByAsset] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await apiCall("GET", `/ai-assets/usage?days=${days}`));
      setError(null);
      // Separate call, and a failure here is not a failure of the page: the actuals are a
      // measurement and the projection is an opinion about them, so the opinion going missing must
      // not take the measurement with it.
      try {
        setForecast(await apiCall("GET", "/ai-assets/usage/forecast"));
      } catch {
        setForecast(null);
      }
      try {
        setSeats(await apiCall("GET", "/ai-assets/usage/seats"));
      } catch {
        setSeats(null);
      }
    } catch (e) {
      // Kept apart from the empty case on purpose. Collapsing them meant a 403, a backend
      // exception or a dropped connection rendered as "No API usage" — telling someone their AI
      // spend is nil because we failed to ask. That is the same false clean bill of health this
      // module refuses to give anywhere else, and here it is about money.
      setData(null);
      setError(e?.response?.data?.message || e?.message || "the request failed");
    } finally {
      setLoading(false);
    }
  }, [apiCall, days]);

  useEffect(() => { load(); }, [load]);

  const series = useMemo(
    () => (data?.daily || []).map((d) => ({
      date: fmtDay(d.date),
      tokens: d.totalTokens,
      cost: d.costUsd,
    })),
    [data],
  );

  // Whether any day in the window carried a reported cost. This only decides whether the
  // Tokens/Spend toggle and the Forecast subtab have a spend chart to offer; the spend chart
  // itself (SpendTrajectoryChart) reads the per-currency `costs` and never plots from `costUsd`.
  const hasSpend = series.some((r) => r.cost != null);

  // Ownerless spend is the sentence this whole capability exists to be able to say, so it is
  // computed up front rather than left for someone to notice in a table.
  // Read, not computed. Summing per-asset costs here was adding currencies together — each asset
  // can be single-currency while differing from the next — which is the same error the headline
  // total avoids, one level down where it is far easier to miss. The server groups it.
  const unowned = useMemo(() => ({
    count: data?.unownedResourceCount ?? 0,
    tokens: data?.unownedTokens ?? 0,
    byCurrency: data?.unownedCostByCurrency ?? [],
  }), [data]);

  const periodTabs = (
    <div className="flex items-center gap-1">
      {PERIODS.map((p) => (
        <button
          key={p.days}
          onClick={() => setDays(p.days)}
          className={`px-2.5 py-1 text-xs rounded-md border ${
            days === p.days
              ? "border-border bg-theme-hover text-content-primary font-medium"
              : "border-transparent text-content-secondary hover:bg-theme-hover"
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );

  if (loading) {
    return (
      <Card>
        <div className="p-6 text-sm text-content-secondary">Loading usage…</div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-content-primary">Usage &amp; Cost</h3>
            {periodTabs}
          </div>
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm text-content-primary">
                Usage could not be loaded — {error}.
              </p>
              <p className="text-xs text-content-secondary mt-1">
                This is a failure to read the ledger, not a report that there was no spend.
              </p>
              <button
                onClick={load}
                className="mt-2 px-2.5 py-1 text-xs rounded-md border border-border hover:bg-theme-hover text-content-primary"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // An empty ledger and a quiet estate are different facts. The backend distinguishes them; saying
  // "no spend" here would throw that distinction away at the last step.
  if (!data?.hasData) {
    return (
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-content-primary">Usage &amp; Cost</h3>
            {periodTabs}
          </div>
          {/* The warning has to come FIRST here, not only on the populated view. "The feed has
              never run" and "there was no spend" are different statements, and the empty state was
              rendering the second while the backend was reporting the first. */}
          {data?.coverageWarning && (
            <div className="flex items-start gap-2 text-xs border border-amber-200 bg-amber-50 text-amber-800 rounded-md p-2 mb-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{data.coverageWarning}</span>
            </div>
          )}
          <p className="text-sm text-content-secondary">
            No API usage recorded for this period. Usage is read daily from the providers you have
            connected; the first read backfills 90 days of history.
          </p>
          {/* Said here rather than left to be inferred. An organization with a large seat estate
              has real AI spend this ledger structurally cannot see, and "no usage" on its own
              invites exactly the wrong conclusion — the one failure this module refuses to make
              anywhere else. */}
          <p className="text-xs text-content-secondary mt-2">
            This measures <span className="font-medium">API and platform consumption</span> billed
            to your connected organizations. Seat-based subscriptions — Copilot, ChatGPT Enterprise,
            Claude Pro — are licensed per user rather than per token, and no provider reports them
            here. An empty period means no API spend, not no AI spend.
          </p>
        </div>
      </Card>
    );
  }

  // Null rather than a fallback: an empty currency from the server means "these span several", and
  // substituting USD is precisely how a euro gets a dollar sign.
  const currency = data.currency || null;

  const viewTabClass = (name) =>
    `px-2.5 py-1 text-xs rounded-md border ${
      view === name
        ? "border-border bg-theme-hover text-content-primary font-medium"
        : "border-transparent text-content-secondary hover:bg-theme-hover"
    }`;
  const chartModeClass = (name) =>
    `px-2 py-0.5 text-[11px] rounded border ${
      chartMode === name
        ? "border-border bg-theme-hover text-content-primary font-medium"
        : "border-transparent text-content-secondary hover:bg-theme-hover"
    }`;

  return (
    <div className="space-y-4">
      <Card>
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-content-primary">Usage &amp; Cost</h3>
            {periodTabs}
          </div>

          {/* The honesty banners render ABOVE the subtabs, on every view: a Consumption total
              with its coverage gap hidden on another subtab is the silent-omission failure this
              module refuses everywhere else. */}
          {data.coverageWarning && (
            <div className="flex items-start gap-2 text-xs border border-amber-200 bg-amber-50 text-amber-800 rounded-md p-2 mb-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{data.coverageWarning}</span>
            </div>
          )}
          {(data.incompleteDayCount > 0 || data.provisionalDayCount > 0) && (
            <div className="flex items-start gap-2 text-xs text-content-secondary border border-border rounded-md p-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div>
                {data.incompleteDayCount > 0 && (
                  <div>
                    {data.incompleteDayCount} day
                    {data.incompleteDayCount === 1 ? " is" : "s are"} excluded from these totals —
                    the provider read did not cover the whole day, so the real figure is higher.
                  </div>
                )}
                {data.provisionalDayCount > 0 && (
                  <div>
                    {data.provisionalDayCount} recent day
                    {data.provisionalDayCount === 1 ? "" : "s"} may still be restated by the
                    provider.
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center gap-1 mb-4">
            <button onClick={() => setView("consumption")} className={viewTabClass("consumption")}>Consumption</button>
            <button onClick={() => setView("forecast")} className={viewTabClass("forecast")}>Forecast</button>
            <button onClick={() => setView("licensing")} className={viewTabClass("licensing")}>Licensing</button>
          </div>

          {view === "consumption" && (
          <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <Stat
              icon={Coins}
              label="Provider-reported spend"
              // A withheld total renders as the per-currency breakdown rather than a single figure.
              // Adding euros to dollars would produce a number that is not money, so there is
              // deliberately no headline when the estate bills in more than one.
              value={data.hasMixedCurrencies
                ? (data.costByCurrency || [])
                    .map((c) => fmtCost(c.costUsd, c.currency)).join(" + ")
                : fmtCost(data.providerReportedCostUsd, currency)}
              sub={data.hasMixedCurrencies
                ? "billed in more than one currency — not combined"
                : `${data.daysWithData} of ${data.daysRequested} days recorded`}
            />
            <Stat
              icon={Cpu}
              label="Tokens"
              value={fmtTokens(data.totalTokens)}
              sub={`${fmtTokens(data.inputTokens)} in · ${fmtTokens(data.outputTokens)} out`}
            />
            <Stat
              icon={UserX}
              label="Spend without an owner"
              // Each currency shown as itself. Where no cost was reported at all, tokens still say
              // something true about scale.
              value={unowned.byCurrency.length > 0
                ? unowned.byCurrency
                    .map((c) => fmtCost(c.costUsd, c.currency)).join(" + ")
                : fmtTokens(unowned.tokens)}
              sub={`${unowned.count} resource${unowned.count === 1 ? "" : "s"}`}
              tone={unowned.count > 0 ? "text-orange-600" : "text-content-primary"}
            />
          </div>
          {/* Vertex AI's limits differ from every other provider's, and its missing spend is a
              read Axaris cannot make, not a zero — said once, only when Vertex AI rows exist. */}
          {(data.byProvider || []).some((p) => p.provider === "VertexAI") && (
            <p className="text-xs text-content-secondary -mt-2 mb-4">
              Google Cloud (Vertex AI) tokens are reported per project, not per agent; input may
              include cached tokens, and history covers about 41 days. Google Cloud spend is not
              reported.
            </p>
          )}

          {/* One chart, one axis at a time. The Tokens/Spend toggle replaces the stacked pair —
              tokens and money never share a plot, for the same reason seat costs never join token
              costs. Spend is the shared SpendTrajectoryChart (settled line, projected tail when
              the server offers one); it carries its own title, so the label row here names only
              the tokens chart. The panel's one 7/30/90 control drives both charts — the spend
              chart is mounted controlled with no selector of its own. The Forecast subtab mounts
              the same chart, with the range and method in the card beside it. */}
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs font-semibold text-content-primary">
              {chartMode === "spend" && hasSpend ? null : "Tokens per day"}
            </div>
            {hasSpend && (
              <div className="flex items-center gap-1">
                <button onClick={() => setChartMode("tokens")} className={chartModeClass("tokens")}>
                  Tokens
                </button>
                <button onClick={() => setChartMode("spend")} className={chartModeClass("spend")}>
                  Spend
                </button>
              </div>
            )}
          </div>
          {chartMode === "spend" && hasSpend ? (
            // Owns its own usage + forecast reads (Decision: one chart, two hosts, identical
            // behaviour). Controlled: the panel's period tabs are the only range control.
            <SpendTrajectoryChart
              orgId={orgId} days={days} onDaysChange={setDays} showSelector={false} height={260}
            />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={series}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={fmtTokens} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value) => [fmtTokens(value), "tokens"]} />
                <Area
                  type="monotone" dataKey="tokens" name="tokens"
                  stroke="#7c3aed" fill="#7c3aed" fillOpacity={0.15}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}

          {/* Model adoption lives with usage now — the standalone dashboard card this replaces
              gave a full-width widget to what is, day to day, a reference list. */}
          {modelsWithheld && (
            <div className="mt-4" data-testid="model-usage-withheld">
              <div className="text-xs font-semibold text-content-primary mb-1.5">Models in use</div>
              <p className="text-xs text-content-secondary">{asSentence(modelUsageUnavailableReason)}</p>
            </div>
          )}
          {models.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-semibold text-content-primary mb-1.5">Models in use</div>
              <div className="flex flex-wrap gap-1.5">
                {models.map((m) => (
                  <span
                    key={m.model}
                    className="px-2 py-1 rounded-md border border-border bg-theme-primary text-xs text-content-primary"
                  >
                    {m.model}
                    {(m.services || []).length > 0 && (
                      <span className="text-content-secondary"> · {m.services.join(", ")}</span>
                    )}
                    {m.assetCount > 0 && (
                      <span className="text-content-secondary">
                        {" "}· {m.assetCount} asset{m.assetCount === 1 ? "" : "s"}
                      </span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* The attribution tables collapsed: they answer "where did it go", which is a question
              someone asks after the totals, not alongside them. */}
          <div className="mt-4 space-y-3">
            <div>
              <button
                onClick={() => setShowByModel((v) => !v)}
                className="flex items-center gap-1 text-xs font-medium text-content-secondary hover:text-content-primary"
              >
                {showByModel ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                By model
              </button>
              {showByModel && (
                <div className="mt-2">
                  {/* Tokens only, and it says so: the provider reports cost per project, not per
                      model, and splitting it across models by token share would invent a precision
                      it never gave us. */}
                  <p className="text-xs text-content-secondary mb-2">
                    Tokens only — providers report cost per project, not per model. Requests show
                    as — where the provider reports tokens but not request counts.
                  </p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-content-secondary text-left">
                        <th className="pb-2">Model</th>
                        <th className="pb-2 text-right">Tokens</th>
                        <th className="pb-2 text-right">Requests</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.byModel || []).slice(0, 10).map((m) => (
                        <tr key={m.model} className="border-t border-border">
                          <td className="py-1.5 text-content-primary">{m.model}</td>
                          <td className="py-1.5 text-right">{fmtTokens(m.totalTokens)}</td>
                          {/* Tokens cannot be spent in zero requests: a zero beside real tokens
                              is a provider that does not report the count, not a count of none. */}
                          <td className="py-1.5 text-right">
                            {m.requestCount === 0 && m.totalTokens > 0 ? "—" : fmtTokens(m.requestCount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div>
              <button
                onClick={() => setShowByAsset((v) => !v)}
                className="flex items-center gap-1 text-xs font-medium text-content-secondary hover:text-content-primary"
              >
                {showByAsset ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                By asset
              </button>
              {showByAsset && (
                <div className="mt-2">
                  <p className="text-xs text-content-secondary mb-2">
                    Usage Axaris could not attribute to a known asset is listed as unattributed
                    rather than hidden. Cost shows as not reported where the provider has not
                    published it yet — Anthropic reports cost only once a UTC day has closed, so
                    today's usage has tokens and no cost until tomorrow — or where an asset bills in
                    more than one currency, which cannot be added together.
                  </p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-content-secondary text-left">
                        <th className="pb-2">Asset</th>
                        <th className="pb-2 text-right">Tokens</th>
                        <th className="pb-2 text-right">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.byAsset || []).slice(0, 10).map((a) => (
                        <tr key={a.aiAssetId ?? "unattributed"} className="border-t border-border">
                          <td className="py-1.5">
                            <span className="text-content-primary">{a.name}</span>
                            {a.aiAssetId != null && !a.ownerUpn && (
                              <span className="ml-2 text-xs text-orange-600">no owner</span>
                            )}
                            {a.aiAssetId == null && (
                              <span className="ml-2 text-xs text-orange-600">unattributed</span>
                            )}
                          </td>
                          <td className="py-1.5 text-right">{fmtTokens(a.totalTokens)}</td>
                          <td className="py-1.5 text-right">{fmtCost(a.costUsd, a.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          </>
          )}

          {view === "forecast" && (
            hasSpend ? (
              // The same SpendTrajectoryChart as Spend mode, controlled by the panel's period
              // tabs: one series per currency, the projected tail only in the currency the
              // forecast is made in. A second spend-series builder here once joined every
              // currency into one line and labelled it with the summary's symbol — the
              // currency-merging error the rest of this module refuses.
              <SpendTrajectoryChart
                orgId={orgId} days={days} onDaysChange={setDays} showSelector={false} height={260}
              />
            ) : (
              <p className="text-sm text-content-secondary">
                No provider-reported spend recorded in this period, so there is nothing to project
                from. Token usage without cost figures cannot be turned into a spend forecast.
              </p>
            )
          )}

          {view === "licensing" && (seats?.skus || []).length === 0 && (
            // An honest empty state, not a hidden tab: no provider that reports seats has been
            // read yet, which is a different statement from "no seat spend".
            <p className="text-sm text-content-secondary">
              No seat licensing has been read from your connected providers. Seat-based
              subscriptions — Copilot, ChatGPT Enterprise, Claude — appear here once a provider
              that reports seat assignments is connected.
            </p>
          )}
        </div>
      </Card>

      {/* The forecast's own currency, not the summary's. The two can legitimately differ: a mixed
          estate reports no summary currency at all, while the projection either declines outright or
          is built from a single currency and knows which. Passing the summary's would label a
          single-currency projection "(mixed currencies)". */}
      {view === "forecast" && forecast && (
        <ForecastCard forecast={forecast} currency={forecast.currency || null} />
      )}
      {view === "licensing" && (seats?.skus || []).length > 0 && (
        <SeatLicensingCard seats={seats} />
      )}
    </div>
  );
}
